import { z } from "zod";

import type { BaseService } from "./BaseService";
import { serviceRequest } from "./BaseService";

/** ******************************************************************************
 *  Schema
 ******************************************************************************* */

export const BillingTypeEnum = z.enum([
  "ONE_TIME_PURCHASE",
  "AUTOMATIC_TOP_UP",
]);

export type BillingType = z.infer<typeof BillingTypeEnum>;

export const BillingStatusEnum = z.enum([
  "COMPLETE",
  "REFUNDED",
  "DISPUTED",
  "REVERSED",
]);

export type BillingStatus = z.infer<typeof BillingStatusEnum>;

export const BillingSchema = z.object({
  _id: z.string(),
  stripeCheckoutSessionId: z.string().nullable(),
  stripePaymentIntent: z.string().nullable(),
  unitAmountSubtotal: z.bigint().positive(),
  type: BillingTypeEnum,
  status: BillingStatusEnum,
  teamId: z.string(),
  userId: z.string(),
  transactionId: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Billing = z.infer<typeof BillingSchema>;

export type BillingServiceInterface = BaseService & {
  hasTeamEverPurchasedCredits(
    request: HasTeamEverPurchasedCreditsRequest,
  ): Promise<HasTeamEverPurchasedCreditsResponse>;
};

export type BillingStoreInterface = {
  hasTeamEverPurchasedCredits(teamId: string): Promise<boolean>;
};

/** ******************************************************************************
 *  Has Team Ever Purchased Credits
 ******************************************************************************* */

export const hasTeamEverPurchasedCreditsParams = z.object({
  teamId: z.string(),
});

export const hasTeamEverPurchasedCredits = serviceRequest.merge(
  z.object({
    params: hasTeamEverPurchasedCreditsParams,
  }),
);

export const hasTeamEverPurchasedCreditsResponse = z.object({
  hasPurchased: z.boolean(),
});

export type HasTeamEverPurchasedCreditsParams = z.infer<
  typeof hasTeamEverPurchasedCreditsParams
>;

export type HasTeamEverPurchasedCreditsRequest = z.infer<
  typeof hasTeamEverPurchasedCredits
>;

export type HasTeamEverPurchasedCreditsResponse = z.infer<
  typeof hasTeamEverPurchasedCreditsResponse
>;
